export enum PinRequestType {
  RegenerateCode = 'regenerateCode',
  ActivateCode = 'activateCode',
  DeleteCode = 'deleteCode',
}

export enum EnumSupportedActions {
  GENERATE_CODE = 'generateCode',
  REGENERATE_CODE = 'regenerateCode',
  ACTIVATE_CODE = 'activateCode',
  REMOVE_CODE = 'removeCode',
  OPEN_DOOR = 'openDoor',
}

export enum MisconductDegree {
  LOW = 'low',
  HIGH = 'high',
  NONE = 'none',
}

export interface PinCodes {
  pinComfortCode?: string;
  backupCode?: string;
  comfortLink?: string;
  mainDoorCode?: string;
  hasComfortCode?: boolean;
  hasBackupCode?: boolean;
  hasMainDoorCode?: boolean;
}

export enum EnumReservationStatus {
  'Confirmed' = 'Confirmed',
  'InHouse' = 'InHouse',
  'CheckedOut' = 'CheckedOut',
  'Canceled' = 'Canceled',
  'NoShow' = 'NoShow',
}

export enum OnlineCheckinStatusType {
  incomplete = 'incomplete',
  complete = 'complete',
  disabled = 'disabled',
}

export interface ReservationDetails {
  reservation: {
    id: string;
    invoiceRecipients?: string[];
    pinCodes: PinCodes;
    urls?: { [key: string]: string };
    canActivateCode?: boolean;
    payment?: Payment;
    arrival: Date;
    departure: Date;
    language: string;
    misconduct?: Misconduct;
    reservationStatus: EnumReservationStatus;
    onlineCheckin?: {
      status: OnlineCheckinStatusType;
      disabledReason?: string;
    };
    manualIdChecked: boolean;
    isVip?: boolean;
    vipCategoryType?: VipCategoryType | null;
    firstReservationId?: string;
    previousReservationId?: string;
    nextReservationId?: string;
  };
  property: {
    mandatoryOnlineCheckIn: boolean;
    manualIdCheckEnabled: boolean;
    timezone: string;
    supportedPincodeActions: EnumSupportedActions[];
  };
}

export enum VipCategoryType {
  INVESTOR = 'INVESTOR',
  LANDLORD = 'LANDLORD',
  JOURNALIST = 'JOURNALIST',
  INFLUENCER = 'INFLUENCER',
  SUPER = 'SUPER',
}

export interface Payment {
  paymentAR?: boolean;
}

export enum LogLevel {
  'Info' = 'Info',
  'Error' = 'Error',
}

export interface EventLog {
  event: string;
  reservationId: string;
  message: string;
  additionalData?: {
    [key: string]: any;
  };
  timestamp?: string;
  timeStamp?: string;
  logLevel: LogLevel;
}

export interface Misconduct {
  types: string[];
  degree: string;
  additionalComment?: string;
}

export interface SuspiciousGuestRecord {
  recordId: string;
  propertyId: string;
  firstName: string;
  lastName: string;
  phoneNumber: string;
  postalCode: string;
  street: string;
  city: string;
  country: string;
  email: string;
  archived: boolean;
  misconductTypes: string[];
  misconductDegree: MisconductDegree;
  reservations: {
    id: string;
    propertyId: string;
  }[];
  misconductComment: string;
}

export interface SuspiciousGuestRecordPaginationFilter {
  limit: number | undefined;
  offsetId?: string;
  offsetPropertyId?: string;
  lastEvaluatedKeyConfig?: {
    id: string;
    propertyId: string;
  };
}

export interface SuspiciousGuestRecordPaginationResult {
  suspiciousGuestRecords: SuspiciousGuestRecord[];
  paginationFilter: SuspiciousGuestRecordPaginationFilter;
}

export interface SelectedMisconductRecords {
  archived: string[];
  active: string[];
}

export enum EnumRecordArchivedState {
  Active = 'active',
  Archived = 'archived',
}

export enum ContentLanguage {
  EN = 'en',
  DE = 'de',
  ES = 'es',
  CZ = 'cz',
  IT = 'it',
  FR = 'fr',
  NL = 'nl',
}

export interface ContentLanguageConfig {
  label: string;
  code: ContentLanguage;
}

export type TranslatedString = {
  [key in ContentLanguage]: string;
};

export enum QueryStrings {
  EVENT_LOGS = 'eventLogs',
  RESERVATION_DETAILS = 'resDetails',
  SUSPICIOUS_GUESTS = 'suspiciousGuests',
  BULK_MESSAGES = 'bulkMessages',
  MESSAGE_LIST = 'messageList',
  HSK_DELAY = 'hskDelay',
  PROOF_OF_ID = 'proofOfId',
  RESERVATION_PIN_CODES = 'resPinCodes',
}

export enum ManualMessageTypes {
  BOOKING_CONFIRMATION = 'bookingConfirmation',
  CHECKIN_INFO = 'checkinInfo',
  CHECKIN_REMINDER = 'checkinReminder',
  INVOICE = 'invoice',
  HOUSE_RULES = 'houseRules',
  CANCELLED = 'cancelled',
  EXTENSION_CONFIRMATION = 'extensionConfirmation',
  WELCOME_MESSAGE = 'welcomeMessage',
  PRE_CHECKOUT_INFO = 'preCheckoutInfo',
  CHECKOUT_REMINDER = 'checkoutReminder',
}

export interface ManualMessageList {
  type: ManualMessageTypes;
  isSent: boolean;
  sentBy?: 'v1' | 'v2';
}

export enum ApiMethods {
  GET = 'get',
  POST = 'post',
  DELETE = 'delete',
  PUT = 'put',
  PATCH = 'PATCH',
}

export interface ApiSignature<T = null> {
  endpoint: string;
  method: ApiMethods;
  payload?: T;
}

export interface GetProofOfIdResponse {
  guests: Array<{
    id: string;
    type: 'primaryGuest' | 'coGuest';
    orderIndex: number;
    presentationName: string;
    documentStatus:
    | 'missing'
    | 'approved'
    | 'incode_approved'
    | 'incode_manual_review_approved'
    | 'incode_manual_review';
    incodeSessionId?: string;
  }>;
  reservationDocuments: Array<{
    downloadUrl: string;
  }>;
}

export interface HskDelayResponse {
  delayNotificationSent: boolean;
  delayUntil: Date;
  reservationId: string;
  unitId: string;
}

export enum CodeType {
  Backup = 'backupCode',
  Comfort = 'comfortCode',
  MainDoor = 'mainDoorCode',
}
